import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { getToken, updateBatchCopy } from '@/services/tap.service.js';
import BrandImageField from './BrandImageField.jsx';

const HEX = /^#[0-9a-fA-F]{6}$/;

// Edit the landing-page copy for a whole batch after the fact. Prefills from
// the batch's current copy (carried on every token row — identical across the
// batch). One save = one server-side UPDATE WHERE batch_label, so the change
// shows on every already-printed tag on next tap. No reprint needed.
function firstNonEmpty(tokens, key) {
  for (const t of tokens || []) {
    if (t[key]) return t[key];
  }
  return '';
}

export default function EditBatchCopyDialog({ open, onOpenChange, label, tokens, onSaved }) {
  const [badge, setBadge] = useState('');
  const [subhead, setSubhead] = useState('');
  const [accent, setAccent] = useState('');
  const [brandImage, setBrandImage] = useState('');
  const [caption, setCaption] = useState('');
  const [saving, setSaving] = useState(false);

  // Seed the form from current batch copy each time the dialog opens. The
  // logo is not on the batch list rows (a data URI per row would be heavy),
  // so it is read off the first token by id.
  useEffect(() => {
    if (!open) return;
    setBadge(firstNonEmpty(tokens, 'badge_text'));
    setSubhead(firstNonEmpty(tokens, 'subhead_text'));
    setAccent(firstNonEmpty(tokens, 'accent_color'));
    setCaption(firstNonEmpty(tokens, 'branding_caption'));
    setBrandImage('');
    const first = tokens && tokens[0];
    if (!first) return;
    let live = true;
    getToken(first.id)
      .then((t) => { if (live && t && t.branding_image_url) setBrandImage(t.branding_image_url); })
      .catch(() => {});
    return () => { live = false; };
  }, [open, tokens]);

  const accentOk = !accent.trim() || HEX.test(accent.trim());

  async function save() {
    if (!accentOk) return;
    setSaving(true);
    try {
      // Empty string clears a field server-side; trim so whitespace == cleared.
      const res = await updateBatchCopy(label, {
        badge_text: badge.trim(),
        subhead_text: subhead.trim(),
        accent_color: accent.trim(),
        branding_image_url: brandImage,
        branding_caption: caption.trim(),
      });
      toast.success(`Copy updated on ${res.updated} tag${res.updated === 1 ? '' : 's'}`);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(`Update failed: ${err.body?.error || err.code || err.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit landing-page copy</DialogTitle>
          <DialogDescription>
            Applies to every tag in “{label}”. Changes show on the next tap — no reprint needed. Leave a field blank to fall back to the default.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-text-dim" htmlFor="ec-badge">
              Badge text
            </label>
            <Input id="ec-badge" maxLength={40} placeholder="Vouched for" value={badge} onChange={(e) => setBadge(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-text-dim" htmlFor="ec-subhead">
              Hero subhead
            </label>
            <Input id="ec-subhead" maxLength={160} placeholder="A pilot put their name on us. Let's get you covered." value={subhead} onChange={(e) => setSubhead(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-text-dim" htmlFor="ec-accent">
              Accent color <span className="text-text-dim/70 normal-case">(hex)</span>
            </label>
            <Input id="ec-accent" placeholder="#0A1D3C" value={accent} onChange={(e) => setAccent(e.target.value)} />
            {!accentOk && <p className="mt-1 text-xs text-accent">Must be a hex color like #0A1D3C.</p>}
          </div>
          <BrandImageField id="ec-brand-image" value={brandImage} onChange={setBrandImage} />
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-text-dim" htmlFor="ec-caption">
              Logo caption
            </label>
            <Input id="ec-caption" maxLength={80} placeholder="UGA Aviation Club" value={caption} onChange={(e) => setCaption(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || !accentOk}>{saving ? 'Saving…' : 'Save copy'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
